import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import Image from '@/components/Image';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { MLProjectTranslation } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectTranslationStatus } from '@/utils/enums';
import { Color } from '@/utils/theme';

const Tr = styled.tr`
  transition: background-color ease-out 0.2s;
  cursor: pointer;
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) => theme.colors.grayscaleExtraLight};
    }
  }
`;

const Thumbnail = styled(Image)`
  width: 90px;
  height: auto;
`;

const ThumbAndTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.div<{ $color: Color }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme, $color }) => theme.colors[$color]};
`;

const TableRow: FC<{ translation: MLProjectTranslation }> = ({
  translation,
}) => {
  const navigate = useNavigate();
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const renderStatus = () => {
    if (translation.status === MLProjectTranslationStatus.DONE) {
      return (
        <Status>
          <Dot $color="success" />
          Done
        </Status>
      );
    }

    return (
      <Status>
        <Dot $color="warning" />
        Pending
      </Status>
    );
  };

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete this translation?`,
        `${translation.language} - ${translation.project!.title}`,
      );

      if (!confirm) return;

      await api.delete(`/v1/distribution/ml-translations/${translation.id}`);

      queryClient.invalidateQueries(['ml_projects']);
      await queryClient.invalidateQueries(['ml_translations']);

      alerts.success('Translation was successfully deleted');
    } catch {
      alerts.error();
    }
  };

  return (
    <Tr
      onClick={() =>
        navigate(`../../../ml-translations/${translation.id}`, {
          relative: 'path',
        })
      }
    >
      <Td>
        <ThumbAndTitle>
          <Thumbnail src={translation.project!.thumbnail?.versions.original} />
          <span>{translation.project!.title}</span>
        </ThumbAndTitle>
      </Td>
      <Td>{renderStatus()}</Td>
      <Td>{DateTime.fromISO(translation.createdAt).toLocaleString()}</Td>
      <Td>{DateTime.fromISO(translation.updatedAt).toLocaleString()}</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default TableRow;
